import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Messages from "@/components/Messages";
import PaymentDialog from "@/components/PaymentDialog";
import { Overline } from "@/components/shared";
import { SuggestedForYou } from "@/components/SuggestedForYou";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SignLeaseDialog, SignDocDialog, CompleteAppDialog } from "@/pages/renter/RenterDialogs";
import { RenterLeasesTab } from "@/pages/renter/RenterLeasesTab";
import { RenterMaintenanceTab } from "@/pages/renter/RenterMaintenanceTab";
import { HomeCard } from "@/pages/renter/HomeCard";
import { RenterPaymentHistory } from "@/pages/renter/RenterPaymentHistory";
import { RenterApplicationsTab } from "@/pages/renter/RenterApplicationsTab";
import { RenterShowingsTab } from "@/pages/renter/RenterShowingsTab";
import {
  FileText, CalendarCheck, Wrench, MessageSquare, ScrollText,
} from "lucide-react";
import { toast } from "sonner";

export default function RenterDashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "leases");
  const [applications, setApplications] = useState([]);
  const [showings, setShowings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);

  const [signLease, setSignLease] = useState(null);
  const [signDoc, setSignDoc] = useState(null); // { lease, doc }
  const [signature, setSignature] = useState("");

  const [maintForm, setMaintForm] = useState({ property_id: "", title: "", description: "", priority: "medium", photo: "" });
  const [maintOpen, setMaintOpen] = useState(false);

  const [payModal, setPayModal] = useState(null);
  const [formApp, setFormApp] = useState(null);
  const [formData, setFormData] = useState({ full_name: "", employer: "", employer_address: "", supervisor_name: "", supervisor_phone: "", annual_income: "", monthly_income: "", move_in_date: "", notes: "" });
  const [formAnswers, setFormAnswers] = useState({});
  const [lastAnswers, setLastAnswers] = useState(null);
  const [appFee, setAppFee] = useState(40);
  const [cardFee, setCardFee] = useState({ percent: 2.9, flat: 0.3 });
  const [payHistory, setPayHistory] = useState([]);
  const [unread, setUnread] = useState(0);
  const [activity, setActivity] = useState({ applications: 0, showings: 0, leases: 0, maintenance: 0 });

  const loadUnread = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  const loadActivity = () => api.get("/activity/counts").then((r) => setActivity(r.data)).catch(() => {});
  const loadPayHistory = () => api.get("/renter/payments").then((r) => setPayHistory(r.data.items || [])).catch(() => {});

  const loadAll = () => {
    api.get("/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/showings").then((r) => setShowings(r.data)).catch(() => {});
    api.get("/leases").then((r) => setLeases(r.data)).catch(() => {});
    api.get("/maintenance").then((r) => setMaintenance(r.data)).catch(() => {});
    api.get("/suggestions").then((r) => setSuggestions(r.data)).catch(() => {});
  };

  const respondSuggestion = async (sid, interested, property_id) => {
    try {
      await api.post(`/suggestions/${sid}/respond`, { interested, property_id });
      toast.success(interested ? "Great! The landlord has been notified — they'll send you a lease, no new application needed." : "Thanks for letting them know.");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send your response");
    }
  };

  useEffect(() => {
    loadAll();
    loadUnread();
    loadActivity();
    loadPayHistory();
    api.get("/payments/config").then((r) => { setAppFee(r.data.application_fee); if (r.data.card_fee) setCardFee(r.data.card_fee); }).catch(() => {});
    const id = setInterval(() => { loadUnread(); loadActivity(); }, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
  }, [searchParams]);

  useEffect(() => {
    if (["applications", "showings", "leases", "maintenance"].includes(activeTab)) {
      loadAll();
      api.post(`/activity/seen/${activeTab}`).then(loadActivity).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const signedLease = leases.find((l) => l.status === "signed");

  const openPay = (config) => setPayModal(config);

  const toggleAutopay = async (lease) => {
    try {
      await api.put(`/leases/${lease.id}/autopay`, { enabled: !lease.autopay_enabled });
      toast.success(!lease.autopay_enabled ? "Auto-Pay enabled — rent will be reminded & one-click ready each month." : "Auto-Pay disabled.");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update Auto-Pay");
    }
  };

  const openForm = (a) => {
    setFormApp(a);
    setFormData({ full_name: a.full_name || user?.name || "", employer: a.employer || "", employer_address: a.employer_address || "", supervisor_name: a.supervisor_name || "", supervisor_phone: a.supervisor_phone || "", annual_income: a.annual_income || "", monthly_income: a.monthly_income || "", move_in_date: a.move_in_date || "", notes: a.notes || "" });
    const init = {};
    (a.template_questions || []).forEach((q) => { init[q.id] = (a.answers || {})[q.id] ?? (q.type === "yes_no" ? "" : ""); });
    setFormAnswers(init);
    setLastAnswers(null);
    api.get("/applications/last-answers").then((r) => { if (r.data.found) setLastAnswers(r.data); }).catch(() => {});
  };

  const useLastAnswers = () => {
    if (!lastAnswers || !formApp) return;
    setFormData((f) => ({ ...f, full_name: lastAnswers.full_name || f.full_name, employer: lastAnswers.employer || f.employer, employer_address: lastAnswers.employer_address || f.employer_address, supervisor_name: lastAnswers.supervisor_name || f.supervisor_name, supervisor_phone: lastAnswers.supervisor_phone || f.supervisor_phone, annual_income: lastAnswers.annual_income || f.annual_income, monthly_income: lastAnswers.monthly_income || f.monthly_income, notes: lastAnswers.notes || f.notes }));
    setFormAnswers((prev) => {
      const next = { ...prev };
      (formApp.template_questions || []).forEach((q) => {
        if (lastAnswers.answers_by_label[q.label] !== undefined) next[q.id] = lastAnswers.answers_by_label[q.label];
      });
      return next;
    });
    toast.success("Filled in your previous answers — review and submit.");
  };

  const submitForm = async () => {
    const req = [
      ["full_name", "full name"], ["employer", "employer"], ["employer_address", "employer address"],
      ["supervisor_name", "supervisor name"], ["supervisor_phone", "supervisor phone"], ["move_in_date", "move-in date"],
    ];
    for (const [k, label] of req) {
      if (!String(formData[k] ?? "").trim()) { toast.error(`Please fill your ${label}.`); return; }
    }
    if (!(parseFloat(formData.annual_income) > 0)) { toast.error("Please enter your annual income."); return; }
    if (!(parseFloat(formData.monthly_income) > 0)) { toast.error("Please enter your monthly income."); return; }
    for (const q of (formApp.template_questions || [])) {
      if (q.required && !String(formAnswers[q.id] ?? "").trim()) { toast.error(`Please answer: ${q.label}`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/applications/${formApp.id}/submit`, {
        full_name: formData.full_name,
        employer: formData.employer,
        employer_address: formData.employer_address,
        supervisor_name: formData.supervisor_name,
        supervisor_phone: formData.supervisor_phone,
        annual_income: parseFloat(formData.annual_income || 0),
        monthly_income: parseFloat(formData.monthly_income || 0),
        move_in_date: formData.move_in_date,
        notes: formData.notes,
        answers: formAnswers,
      });
      toast.success("Application submitted to the landlord for review!");
      setFormApp(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit application");
    } finally {
      setBusy(false);
    }
  };


  const doSign = async () => {
    if (!signature.trim()) return;
    setBusy(true);
    try {
      await api.post(`/leases/${signLease.id}/sign`, { signature });
      toast.success("Lease signed! You can now pay your security deposit.");
      setSignLease(null);
      setSignature("");
      loadAll();
    } catch (e) {
      toast.error("Could not sign lease");
    } finally {
      setBusy(false);
    }
  };

  const doSignDoc = async () => {
    if (!signature.trim()) { toast.error("Please type your full name to sign."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/leases/${signDoc.lease.id}/documents/${signDoc.doc.id}/sign`, { signature });
      toast.success(data.status === "signed" ? "All documents signed! You can now pay your deposit." : `Signed “${signDoc.doc.name}”.`);
      setSignDoc(null);
      setSignature("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign document");
    } finally {
      setBusy(false);
    }
  };

  const respondShowing = async (id, payload) => {
    try {
      await api.post(`/showings/${id}/respond`, payload);
      const msg = { confirm: "Showing confirmed!", propose: "New time proposed — sent to the landlord", decline: "Showing cancelled", complete: "Showing completed" }[payload.action] || "Showing updated";
      toast.success(msg);
      loadAll();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update showing");
      return false;
    }
  };


  const submitMaint = async () => {
    setBusy(true);
    try {
      await api.post("/maintenance", maintForm);
      toast.success("Maintenance request submitted");
      setMaintOpen(false);
      setMaintForm({ property_id: "", title: "", description: "", priority: "medium", photo: "" });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMaintForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const leaseProperties = leases.map((l) => ({ id: l.property_id, title: l.property_title }));

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <Overline>Renter dashboard</Overline>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink">Hi, {user.name.split(" ")[0]}</h1>

        <SuggestedForYou suggestions={suggestions} onRespond={respondSuggestion} />

        <HomeCard signedLease={signedLease} cardFee={cardFee} openPay={openPay} toggleAutopay={toggleAutopay} />

        <RenterPaymentHistory payHistory={payHistory} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="bg-white border border-line rounded-xl p-1 h-auto flex flex-nowrap overflow-x-auto no-scrollbar md:flex-wrap justify-start gap-1">
            {[
              ["leases", "My Lease", ScrollText],
              ["applications", "Applications", FileText],
              ["showings", "Showings", CalendarCheck],
              ["maintenance", "Maintenance", Wrench],
              ["messages", "Messages", MessageSquare],
            ].map(([v, label, Icon]) => (
              <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="shrink-0 data-[state=active]:bg-forest data-[state=active]:text-white rounded-lg px-4 py-2 text-sm gap-2">
                <Icon className="h-4 w-4" /> {label}
                {v === "messages" && unread > 0 && (
                  <span data-testid="messages-unread-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{unread}</span>
                )}
                {v !== "messages" && (activity[v] || 0) > 0 && (
                  <span data-testid={`tab-badge-${v}`} className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[11px] font-semibold">{activity[v]}</span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Leases */}
          <TabsContent value="leases" className="mt-6">
            <RenterLeasesTab
              leases={leases}
              onSignDoc={(l, d) => { setSignDoc({ lease: l, doc: d }); setSignature(""); }}
              onSignLease={(l) => setSignLease(l)}
            />
          </TabsContent>

          {/* Applications */}
          <TabsContent value="applications" className="mt-6">
            <RenterApplicationsTab applications={applications} appFee={appFee} openPay={openPay} openForm={openForm} />
          </TabsContent>

          {/* Showings */}
          <TabsContent value="showings" className="mt-6">
            <RenterShowingsTab showings={showings} respondShowing={respondShowing} />
          </TabsContent>

          {/* Maintenance */}
          <TabsContent value="maintenance" className="mt-6">
            <RenterMaintenanceTab
              maintenance={maintenance} leaseProperties={leaseProperties}
              maintForm={maintForm} setMaintForm={setMaintForm} maintOpen={maintOpen} setMaintOpen={setMaintOpen}
              onPhoto={onPhoto} submitMaint={submitMaint} busy={busy}
            />
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages" className="mt-6">
            <Messages onActivity={loadUnread} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Sign lease / sign document / complete application dialogs */}
      <SignLeaseDialog signLease={signLease} setSignLease={setSignLease} signature={signature} setSignature={setSignature} busy={busy} doSign={doSign} userName={user.name} />
      <SignDocDialog signDoc={signDoc} setSignDoc={setSignDoc} signature={signature} setSignature={setSignature} busy={busy} doSignDoc={doSignDoc} />
      <CompleteAppDialog formApp={formApp} setFormApp={setFormApp} formData={formData} setFormData={setFormData} formAnswers={formAnswers} setFormAnswers={setFormAnswers} lastAnswers={lastAnswers} useLastAnswers={useLastAnswers} busy={busy} submitForm={submitForm} />


      {payModal && (
        <PaymentDialog
          open={!!payModal}
          onOpenChange={(o) => !o && setPayModal(null)}
          purpose={payModal.purpose}
          amount={payModal.amount}
          label={payModal.label}
          lease_id={payModal.lease_id}
          application_id={payModal.application_id}
          allowPartial={payModal.allowPartial}
          maxAmount={payModal.maxAmount}
          onSuccess={loadAll}
        />
      )}
    </div>
  );
}
